import JsBarcode from 'jsbarcode';

export type BarcodeRenderOptions = {
  value: string;
  format: string;
  barWidth: number;
  height: number;
  margin: number;
  lineColor: string;
  background: string;
  /** Scale factor for higher resolution output (default: 4 for print quality) */
  scaleFactor?: number;
};

export type BarcodeResult = {
  dataUrl: string;
  /** Display width in pixels (actual canvas width / scale factor) */
  width: number;
  /** Display height in pixels (actual canvas height / scale factor) */
  height: number;
};

// Default scale factor for high-quality output (4x = good for 300+ DPI printing)
const DEFAULT_SCALE_FACTOR = 4;

/**
 * Generate a barcode as a data URL using JsBarcode.
 * Returns an object with the PNG data URL and the display dimensions.
 * The barcode is rendered at a higher resolution (scaleFactor) for print quality,
 * but dimensions returned are the display size.
 */
export const generateBarcodeDataUrl = (options: BarcodeRenderOptions): BarcodeResult => {
  const canvas = document.createElement('canvas');
  const scaleFactor = options.scaleFactor ?? DEFAULT_SCALE_FACTOR;

  try {
    // Render at scaled resolution for higher quality
    JsBarcode(canvas, options.value || ' ', {
      format: options.format || 'CODE128',
      width: (options.barWidth || 2) * scaleFactor,
      height: (options.height || 50) * scaleFactor,
      margin: (options.margin ?? 10) * scaleFactor,
      lineColor: options.lineColor || '#000000',
      background: options.background || '#FFFFFF',
      displayValue: false, // no text below barcode
    });
  } catch (e) {
    // If barcode generation fails (e.g., invalid data for format), return a placeholder
    console.warn('Barcode generation failed:', e);
    // Draw a simple error placeholder
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = 100 * scaleFactor;
      canvas.height = (options.height || 50) * scaleFactor;
      ctx.fillStyle = options.background || '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ff0000';
      ctx.font = `${10 * scaleFactor}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('Invalid', canvas.width / 2, canvas.height / 2);
    }
  }

  // Return display dimensions (canvas size / scale factor)
  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width / scaleFactor,
    height: canvas.height / scaleFactor,
  };
};
